// src/components/TopContact.jsx
// Owner-eis 2026-10-11: op ELKE pagina bovenaan makkelijk contact — formulier
// en e-mail (WhatsApp op verzoek van de eigenaar verwijderd 2026-10-30).
// Deze module levert de bouwstenen voor de gedeelde <Header>
// (die op elke route rendert):
//   - TopMailLink                   — mailto, prerender-safe (zie EmailLink.js)
//   - QuickContactPanel             — kort formulier, POST naar bestaande /api/contact
// Guard: scripts/__tests__/top-contact-guard.test.js

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle, Send, X } from 'lucide-react';
import { CONTACT_EMAIL } from './EmailLink';
import { trackEvent } from '../lib/analytics';
import EmailHint from './EmailHint';
import { useEmailHint } from '../lib/useEmailHint';
import { useFormFunnel } from '../lib/useFormFunnel';

const INTEREST_CHIPS = ['OBD-stekker tracker', 'Accu-tracker', 'Advies gewenst'];
const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

/**
 * mailto-knop. Cloudflare's e-mail-obfuscation herschrijft elk adres in de
 * geserveerde HTML naar een dode /cdn-cgi/-link (zie EmailLink.js), dus het
 * adres staat NIET in de prerender: tot na mount wijst de knop naar /contact/
 * (werkt ook zonder JS), daarna naar mailto:.
 */
export function TopMailLink({ className, id, children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const live = mounted && !isPrerender;
  return (
    <a
      href={live ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Vraag over GPS tracking')}` : '/contact/'}
      onClick={() => trackEvent('email_click', { location: 'header' })}
      className={className}
      id={id}
      aria-label="Stuur ons een e-mail"
    >
      {children}
    </a>
  );
}

const PHONE_DIGITS = /\d/g;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Eén veld "telefoon of e-mail": '@' → e-mail, anders telefoon (min. 8 cijfers). */
export function parseContactField(raw) {
  const v = String(raw || '').trim();
  if (v.includes('@')) return EMAIL_RE.test(v) ? { email: v, phone: '' } : null;
  return (v.match(PHONE_DIGITS) || []).length >= 8 ? { email: '', phone: v } : null;
}

/** Het formulier zelf (state + submit). Eén implementatie voor dropdown én hero-kaart. */
function QuickContactForm({ autoFocus = false, idPrefix = 'qc' }) {
  const { pathname } = useLocation();
  const [form, setForm] = useState({ name: '', contact: '', message: '', website: '' });
  const [chips, setChips] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [fieldError, setFieldError] = useState('');
  const nameRef = useRef(null);
  const funnel = useFormFunnel('header_quick');
  const emailHint = useEmailHint(form.contact, (v) => setForm((p) => ({ ...p, contact: v })));

  useEffect(() => {
    if (autoFocus && nameRef.current) nameRef.current.focus();
  }, [autoFocus]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggleChip = (c) => setChips((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const submit = async (e) => {
    e.preventDefault();
    const parsed = parseContactField(form.contact);
    if (!parsed) {
      funnel.error('contact');
      setFieldError('Vul een geldig telefoonnummer of e-mailadres in.');
      return;
    }
    setFieldError('');
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: parsed.email,
          phone: parsed.phone,
          message: form.message.trim(),
          systemType: chips.join(', '),
          service: 'GPS Tracking',
          type: 'Snelle aanvraag (header)',
          page: pathname,
          website: form.website, // honeypot
        }),
      });
      if (!res.ok) throw new Error('request failed');
      trackEvent('lead_submit', { location: 'header_quick_form' });
      funnel.submitted();
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const input = 'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 text-base';

  return (
    <>
        {status === 'sent' ? (
          <div className="text-center py-6" role="status">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="h-8 w-8 text-emerald-600" aria-hidden="true" />
            </div>
            <p className="text-lg font-black text-emerald-900">Bedankt, {form.name.trim()}!</p>
            <p className="text-slate-600 text-sm">Wij nemen op werkdagen binnen 24 uur contact met u op.</p>
          </div>
        ) : (
          <form onFocus={funnel.onFocus} onSubmit={submit} className="space-y-3" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${idPrefix}-name`} className="block text-sm font-bold text-slate-700 mb-1">Naam *</label>
                <input id={`${idPrefix}-name`} ref={nameRef} type="text" required autoComplete="name" value={form.name} onChange={set('name')} className={input} placeholder="Uw naam" />
              </div>
              <div>
                <label htmlFor={`${idPrefix}-contact`} className="block text-sm font-bold text-slate-700 mb-1">Telefoon of e-mail *</label>
                <input id={`${idPrefix}-contact`} type="text" required inputMode="email" autoComplete="email" value={form.contact} onChange={(e) => { emailHint.hide(); set('contact')(e); }} onBlur={emailHint.onBlur} className={input} placeholder="06 1234 5678 of uw@email.nl" aria-invalid={!!fieldError} />
                <EmailHint suggestion={emailHint.suggestion} onApply={emailHint.apply} />
              </div>
            </div>
            {fieldError && <p className="text-sm text-red-600" role="alert">{fieldError}</p>}

            <fieldset>
              <legend className="block text-sm font-bold text-slate-700 mb-1">Waar gaat het om? (optioneel)</legend>
              <div className="flex flex-wrap gap-2">
                {INTEREST_CHIPS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={chips.includes(c)}
                    onClick={() => toggleChip(c)}
                    className={`min-h-[44px] px-4 rounded-xl border text-sm font-semibold transition-all ${chips.includes(c) ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor={`${idPrefix}-message`} className="block text-sm font-bold text-slate-700 mb-1">Bericht (optioneel)</label>
              <textarea id={`${idPrefix}-message`} rows={2} maxLength={500} value={form.message} onChange={set('message')} className={input} placeholder="Bijv. aantal voertuigen of uw vraag" />
            </div>

            {/* Honeypot — leeg bij echte bezoekers */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor={`${idPrefix}-website`}>Website</label>
              <input id={`${idPrefix}-website`} type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
            </div>

            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full min-h-[52px] inline-flex items-center justify-center px-6 py-3 text-base font-bold rounded-xl text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 shadow-sm transition-all active:scale-95"
            >
              <Send className="h-4 w-4 mr-2" aria-hidden="true" />
              {status === 'sending' ? 'Bezig met versturen…' : 'Verstuur aanvraag'}
            </button>

            {status === 'error' && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800" role="alert">
                Versturen is niet gelukt. Neem direct contact op:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold underline">{CONTACT_EMAIL}</a>.
              </div>
            )}
          </form>
        )}
    </>
  );
}

/** Dropdown onder de header (vanaf elke pagina te openen met de Offerte-knop). */
export function QuickContactPanel({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      id="header-quick-form"
      className="absolute left-0 right-0 top-full bg-white border-b border-slate-200 shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto"
      role="region"
      aria-label="Snel contact of offerte aanvragen"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-lg font-black text-slate-900">Gratis offerte of terugbelverzoek</p>
            <p className="text-sm text-slate-500">Reactie op werkdagen binnen 24 uur.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 -mr-2 text-slate-500 hover:text-slate-800" aria-label="Formulier sluiten">
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <QuickContactForm autoFocus />
      </div>
    </div>
  );
}

/** Kaart in de hero (zelfde kaartstijl als de rest van de site: wit, rounded-3xl, border, shadow-xl). */
export function HeroContactCard() {
  return (
    <div id="hero-contact-card" className="bg-white rounded-3xl border border-slate-200 shadow-xl p-5 sm:p-6 scroll-mt-24">
      <p className="text-xl font-black text-slate-900">Gratis offerte of terugbelverzoek</p>
      <p className="text-sm text-slate-500 mb-4">Reactie op werkdagen binnen 24 uur.</p>
      <QuickContactForm idPrefix="hc" />
    </div>
  );
}
